import { Targeting } from "@foundation/sparkle/lib/AdTargeting/TargetingProvider";
import {
  type AdRefreshOptions,
  type SparkleProviderProps,
  type AdContextData,
} from "@foundation/sparkle/lib/types";
import { type AdDemand } from "~types/types";

const DEFAULT_ADCODE = "markets/custom";
const PAGE_TYPE_TO_ADCODE: Record<string, string> = {
  "stocks-bazaar": "markets/stocks/index",
  "quote-bazaar": "markets/quote",
  "watchlist-bazaar": "markets/watchlist",
};

export const getAdOptions = (
  tickerId?: string
): { isDecoupled: boolean; adContextData: AdContextData } => {
  return {
    isDecoupled: true,
    adContextData: {
      adPageType: "quote",
      tickerId,
    },
  };
};

const NINETY_SECS = 90 * 1000;
const SIXTY_SECS = 60 * 1000;
const FORTY_FIVE_SECS = 45 * 1000;

export const REFRESH_INTERVALS: Record<AdDemand, number> = {
  // Should have no refresh at extra low; 0 evaluates to false and will not run sparkle.useAdRefresh()
  "extra-low": 0,
  low: NINETY_SECS,
  medium: SIXTY_SECS,
  high: FORTY_FIVE_SECS,
};

export const AD_DOUBLEVERIFY_SCRIPT_PERF_IMPROVEMENT =
  "https://pub.doubleverify.com/dvtag/24434720/DV1462763/pub.js";

export const getAdCode = (
  pageType: string,
  analyticsAdCode?: string,
  locale?: string
) => {
  const base = locale === "ja" ? "japan" : "bloomberg";

  if (analyticsAdCode) {
    return `${base}/${analyticsAdCode}`;
  }

  return `${base}/${PAGE_TYPE_TO_ADCODE[pageType] || DEFAULT_ADCODE}`;
};

const AD_DEMAND = process.env.NEXT_PUBLIC_AD_DEMAND as AdDemand;

export const SPARKLE_BREAKPOINTS = {
  mobile: 0,
  tablet: 768,
  small_desktop: 1123,
  large_desktop: 1335,
};

export const getSparkleProviderOptions = (
  pageType: string,
  analyticsAdCode?: string,
  isDecoupled?: boolean,
  adContextData?: AdContextData,
  adTargeting?: Targeting,
  locale?: string
): Omit<SparkleProviderProps, "children"> =>
  ({
    defaultAdCode: getAdCode(pageType, analyticsAdCode, locale),
    sparkleInitOptions: {
      pageTargeting: { page: pageType, ...adTargeting },
      breakpoints: SPARKLE_BREAKPOINTS,
      useCachedConsent: true,
      disableIAS: true,
      enableBombora: true,
      isDecoupled,
      adContextData,
      excludeTargetingTypes: ["content"],
      dvScriptUrl: AD_DOUBLEVERIFY_SCRIPT_PERF_IMPROVEMENT,
    },
  } as Omit<SparkleProviderProps, "children">);

export const getAdRefreshOptions = (adDemand = AD_DEMAND): AdRefreshOptions => {
  const refreshInterval = REFRESH_INTERVALS[adDemand] ?? NINETY_SECS;

  return { refreshInterval };
};

export const getContentMetadata = (id: string, mediaSecurityType: string) => ({
  contentType: mediaSecurityType,
  id,
  contentTags: [
    {
      id,
      type: mediaSecurityType,
    },
  ],
});
